import { isEmpty, isNil, isUndefined } from 'lodash';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fieldFromComponentData } from '@shared/data/entity/fields/fieldFromComponentData';
import { CardFormType } from '@shared/scenes/entity/cardFormTypeContext';
import { CardEntityError, useFormErrors } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, GetUpdatedField, UpdateField } from '@shared/scenes/entity/types';
import { useViewComponents } from '@shared/scenes/entity/util/useViewComponents';
import { useViewTemplateLayout, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import {
  isLayoutComponent,
  isLayoutSection,
  isSubformWorkflowComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { SupportedSubformComponentValue } from '@shared/types/fieldValues';
import { useSubformViewTemplate } from './useSubformViewTemplate';

export interface SubformData {
  workflowTemplate: API.WorkflowTemplateFragment | undefined;
  viewTemplate: API.ViewTemplateFragment | undefined;
  components: API.AnyComponent[];
  orderedComponents: API.AnyComponent[];
  fields: FieldRecord;
  updateField: UpdateField;
  errors: CardEntityError[];
  isValid: boolean;
  isInitialized: boolean;
  submitAttempted: boolean;
  setSubmitAttempted: Dispatch<SetStateAction<boolean>>;
}

export function useSubformData({
  workflowTemplateId,
  initialData,
  type,
  apiErrors,
  isReadOnly,
}: {
  workflowTemplateId: string;
  initialData?: Record<string, SupportedSubformComponentValue>;
  type?: CardFormType;
  /** Server-side field-level errors to surface within the inner subform (e.g.
   *  COMPONENT_DATA_REQUIRED errors from the parent form save). Each error's
   *  componentId should match an inner subform component. */
  apiErrors?: CardEntityError[];
  isReadOnly?: boolean;
}): SubformData {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const components = useMemo(
    () => workflowTemplate?.components.filter(isSubformWorkflowComponent) ?? [],
    [workflowTemplate?.components],
  );

  const [fields, setFields] = useState<FieldRecord>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Initialize (and reset) fields whenever workflowTemplateId changes, waiting for
  // components to finish loading. initialData is captured at that moment.
  const lastInitializedId = useRef('');
  useEffect(() => {
    if (
      !workflowTemplateId ||
      workflowTemplateId === lastInitializedId.current ||
      isEmpty(components)
    ) {
      return;
    }

    lastInitializedId.current = workflowTemplateId;
    setFields(initFieldRecord(components, initialData));
    setIsInitialized(true);
    setSubmitAttempted(false);
    // initialData intentionally omitted — we only want to capture it at init time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowTemplateId, components]);

  // Reset the init guard when the subform route is no longer active so that
  // re-opening the same template re-initializes from fresh initialData (e.g.
  // after the user removes and re-adds the same subform).
  useEffect(() => {
    if (!workflowTemplateId) {
      lastInitializedId.current = '';
    }
  }, [workflowTemplateId]);

  const viewTemplate = useSubformViewTemplate({
    workflowTemplateId,
    type: type ?? 'EDIT',
  });
  const { layout } = useViewTemplateLayout(workflowTemplateId, viewTemplate?.id);

  // Use the same hook chain as useEditCardEntity: useVisibleComponents (filters isHidden) →
  // applyComponentOverrides (merges required/readonly/isHidden overrides onto components).
  const allViewComponents = useViewComponents({ workflowTemplate, viewTemplate, isReadOnly });
  const effectiveComponents = useMemo(
    () => allViewComponents.filter(isSubformWorkflowComponent),
    [allViewComponents],
  );

  const { errors, isValid } = useFormErrors({
    fields,
    components: effectiveComponents,
    apiErrors,
    includeOnSubmitErrors: submitAttempted,
    includeLocalErrors: true,
  });

  const updateField = useCallback(
    (componentId: string, fieldOrUpdater: Parameters<UpdateField>[1]) => {
      if (typeof fieldOrUpdater === 'function') {
        const updater = fieldOrUpdater as GetUpdatedField;
        setFields(prev => ({ ...prev, [componentId]: updater(prev[componentId] ?? null) }));
      } else {
        setFields(prev => ({ ...prev, [componentId]: fieldOrUpdater }));
      }
    },
    [],
  );

  const orderedComponents = useMemo(
    () => applyLayoutOrder(effectiveComponents, layout),
    [effectiveComponents, layout],
  );

  return {
    workflowTemplate,
    viewTemplate,
    components: effectiveComponents,
    orderedComponents,
    fields,
    updateField,
    errors,
    isValid,
    isInitialized,
    submitAttempted,
    setSubmitAttempted,
  };
}

function applyLayoutOrder(
  components: API.AnyComponent[],
  layout?: API.ViewTemplateLayoutFragment | null,
): API.AnyComponent[] {
  if (isNil(layout)) {
    return components;
  }

  const componentMap = new Map(components.map(c => [c.id, c]));
  const ordered: API.AnyComponent[] = [];
  const seen = new Set<string>();

  for (const item of layout.items ?? []) {
    if (isLayoutComponent(item) && item.componentId) {
      const component = componentMap.get(item.componentId);
      if (component && !seen.has(component.id)) {
        ordered.push(component);
        seen.add(component.id);
      }
    } else if (isLayoutSection(item)) {
      for (const componentId of item.componentIds ?? []) {
        const component = componentMap.get(componentId);
        if (component && !seen.has(component.id)) {
          ordered.push(component);
          seen.add(component.id);
        }
      }
    }
  }

  for (const component of components) {
    if (!seen.has(component.id)) {
      ordered.push(component);
    }
  }

  return ordered;
}

function initFieldRecord(
  components: API.AnyComponent[],
  initialData?: Record<string, SupportedSubformComponentValue>,
): FieldRecord {
  const record: FieldRecord = {};
  for (const component of components) {
    const raw = applyDefaultValue(component, initialData?.[component.id]);
    record[component.id] = fieldFromComponentData(component, raw);
  }
  return record;
}

function applyDefaultValue(
  component: API.AnyComponent,
  initialDataValue?: SupportedSubformComponentValue,
): SupportedSubformComponentValue | undefined {
  if (isTextComponent(component) && isUndefined(initialDataValue)) {
    return component.default?.text;
  }
  return initialDataValue;
}
